// Pseudo-localization for the `en-XA` QA locale. Wraps each translation in
// `⟦ ... ⟧` markers, replaces ASCII letters with accented look-alikes, and
// pads ~30% extra length. Reveals truncation, hardcoded strings, and string
// concat bugs without waiting for translators.
//
// Tokens that must NOT be transformed (would break ICU parsing / Trans slots):
// - ICU placeholders / plural / select:  {date}, {count, plural, one {…} other {#…}}
// - Trans tags, numeric and named:       <0>, </0>, <0 />, <link>, </link>, <link />
// - i18next nesting:                     $t(common:foo.bar)

const ASCII_MAP: Record<string, string> = {
  a: "ä",
  b: "ƀ",
  c: "ç",
  d: "đ",
  e: "é",
  f: "ƒ",
  g: "ǵ",
  h: "ĥ",
  i: "í",
  j: "ǰ",
  k: "ķ",
  l: "ļ",
  m: "ɱ",
  n: "ñ",
  o: "ö",
  p: "þ",
  q: "ǫ",
  r: "ŕ",
  s: "š",
  t: "ť",
  u: "ü",
  v: "v",
  w: "ŵ",
  x: "x",
  y: "ý",
  z: "ž",
  A: "Ä",
  B: "Ɓ",
  C: "Ç",
  D: "Đ",
  E: "É",
  F: "Ƒ",
  G: "Ǵ",
  H: "Ĥ",
  I: "Í",
  J: "J",
  K: "Ķ",
  L: "Ļ",
  M: "M",
  N: "Ñ",
  O: "Ö",
  P: "Þ",
  Q: "Q",
  R: "Ŕ",
  S: "Š",
  T: "Ť",
  U: "Ü",
  V: "V",
  W: "Ŵ",
  X: "X",
  Y: "Ý",
  Z: "Ž",
};

const TAG_RE = /^<\/?[A-Za-z0-9_]+\s*\/?>/;

// Walk the string once, copying preserve-ranges (ICU groups, Trans tags, $t nesting)
// verbatim and accent-transforming everything else. Brace-balanced so nested ICU
// (plural inside select, etc.) is preserved as a single span.
const transformLeaf = (text: string): string => {
  let result = "";
  let i = 0;
  while (i < text.length) {
    const c = text.charAt(i);

    if (c === "{") {
      let depth = 0;
      let j = i;
      while (j < text.length) {
        const ch = text.charAt(j);
        if (ch === "{") depth++;
        else if (ch === "}") {
          depth--;
          if (depth === 0) {
            j++;
            break;
          }
        }
        j++;
      }
      result += text.slice(i, j);
      i = j;
      continue;
    }

    if (c === "<") {
      const m = TAG_RE.exec(text.slice(i));
      if (m) {
        result += m[0];
        i += m[0].length;
        continue;
      }
    }

    if (c === "$" && text.startsWith("$t(", i)) {
      const close = text.indexOf(")", i + 3);
      if (close >= 0) {
        result += text.slice(i, close + 1);
        i = close + 1;
        continue;
      }
    }

    result += ASCII_MAP[c] ?? c;
    i++;
  }
  return result;
};

const pseudoLocalize = (text: string): string => {
  const padding = "~".repeat(Math.ceil(text.length * 0.3));
  return `⟦${transformLeaf(text)}${padding}⟧`;
};

export const pseudoLocalizeBundle = (data: unknown): unknown => {
  if (typeof data === "string") return pseudoLocalize(data);
  if (Array.isArray(data)) return data.map(pseudoLocalizeBundle);
  if (data && typeof data === "object") {
    return Object.fromEntries(
      Object.entries(data as Record<string, unknown>).map(([k, v]) => [k, pseudoLocalizeBundle(v)]),
    );
  }
  return data;
};
